import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable, Subscription, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, take, tap } from 'rxjs/operators';

import { CompanyService, isTruthy } from '@app/shared';
import { companiesPageSetFilter } from '@modules/companies/store/companies-page.actions';
import { CompaniesPageFilterState } from '@modules/companies/store/companies-page.reducer';
import * as FormActions from '@shared/form/store/form.actions';
import { isModalConfirmed } from '@shared/modal';
import { getCompaniesPage, getCompaniesPageFilter } from '../store/companies-page.selectors';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'mojo-companies-filters',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CompaniesFilterComponent implements OnInit, OnDestroy {
  @Output() searchCompaniesOut = new EventEmitter<string>();
  searchCompanies = new FormControl('');
  subs = new Subscription();
  filters$: Observable<CompaniesPageFilterState> = this.store.pipe(
    select(getCompaniesPageFilter),
    tap((filters) => {
      if (!this.initialState && this.isModalContent) {
        this.initialState = { ...filters };
      }
    }),
  );

  totalElements$: Observable<number> = this.store.pipe(
    select(getCompaniesPage),
    map((pagination) => (!!pagination.totalElements ? pagination.totalElements : 0)),
  );

  @Input() isModalContent = true;

  private modalClosedSubscription = this.store
    .pipe(select(isModalConfirmed), filter(isTruthy))
    .subscribe(() => {
      if (this.isModalContent) {
        this.applyFilter(this.initialState);
      }
    });

  private initialState: CompaniesPageFilterState = null;

  constructor(private store: Store<{}>, public companyService: CompanyService) {}
  ngOnInit(): void {
    this.setupSearchFilter();
  }

  setupSearchFilter() {
    this.filters$.pipe(take(1)).subscribe((filters) => {
      if (filters.name) {
        this.searchCompanies.setValue(filters.name, { emitEvent: false });
      }
    });

    this.subs.add(
      this.searchCompanies.valueChanges
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((term) => {
          const trimmedTerm = term?.trim() || '';
          this.searchCompaniesOut.emit(trimmedTerm);
          const filterState: Partial<CompaniesPageFilterState> = {
            name: trimmedTerm,
          };
          this.store.dispatch(companiesPageSetFilter({ payload: filterState }));
        }),
    );
  }

  applyFilter(filterState: Partial<CompaniesPageFilterState>): void {
    filterState.name = this.searchCompanies.value ? this.searchCompanies.value : '';
    this.store.dispatch(companiesPageSetFilter({ payload: filterState }));
  }

  submit(): void {
    this.unsubscribe();
    this.store.dispatch(FormActions.formSubmit({}));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.unsubscribe();
  }

  private unsubscribe(): void {
    return !!this.modalClosedSubscription && this.modalClosedSubscription.unsubscribe();
  }
}
